import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

import CaseStudyPropTypes from '../models/CaseStudy/CaseStudyPropTypes';

import { GatsbyImage } from 'gatsby-plugin-image';
import { Heading, InternalLink, Label, Text, animation, colors, media } from '../ui-components';

const propTypes = {
  caseStudy: CaseStudyPropTypes,
  displaySnippetText: PropTypes.bool,
  smallDesktopCard: PropTypes.bool
};

/**
 * Component that renders a case study card
 *
 * @component CaseStudyCard 
 * 
 * @param {object} props
 * 
 * @prop {object} caseStudy
 * @prop {boolean} displaySnippetText
 * @prop {boolean} smallDesktopCard
 * 
 * @return {JSX}
 */
const CaseStudyCard = props => {
  const { caseStudy, displaySnippetText = false, smallDesktopCard = false } = props;

  return (
    <WrapperLink to={`/case-studies/${caseStudy.slug}`}>
      {caseStudy?.heroImageData?.fluid && <HeroImg image={caseStudy.heroImageData.fluid} smallDesktopCard={smallDesktopCard} alt={caseStudy.title}/>}
      <Wrapper smallDesktopCard={smallDesktopCard}>
        <Content>
          <ClientLabel size="small">{caseStudy.clientName}</ClientLabel>
          <TitleHeading level={5}>{caseStudy.title}</TitleHeading>
          {displaySnippetText && <SnippetText smallDesktopCard={smallDesktopCard}>{caseStudy.snippet}</SnippetText>}
          <CategoriesText>{caseStudy?.categories && caseStudy?.categories?.join(', ')}</CategoriesText>
        </Content>
      </Wrapper>
    </WrapperLink>
  );
};

CaseStudyCard.propTypes = propTypes;

const WrapperLink = styled(props => <InternalLink {...props} />)`
  display: flex;
  flex-direction: column;
  text-decoration: none;
  text-transform: none;
  width: 100%;
  overflow: hidden;
  align-items: inherit;
`;

const HeroImg = styled(GatsbyImage)`
  display: ${({ smallDesktopCard }) => (smallDesktopCard ? 'none' : '')};
  width: 100%;
  height: 344px;
  margin-bottom: -40px;
  transition: transform ${animation.general.durationAndTiming};

  ${WrapperLink}:hover & {
    transform: scale(1.035);
  }

  @media only screen and (max-width: ${media.largeTop}) {
    height: 280px;
  }

  @media only screen and (max-width: ${media.mediumTop}) {
    height: 170px;
    margin-bottom: 0;
  }

  @media only screen and (max-width: ${media.smallTop}) {
    display: flex;
  }
`;

const Wrapper = styled.div`
  position: relative;
  width: ${({ smallDesktopCard }) => (smallDesktopCard ? '100%' : '90%')};
  padding: 40px 32px;
  background-color: white;
  flex: 1 0 auto;
  transition: background-color ${animation.general.durationAndTiming};

  ${WrapperLink}:hover & {
    background-color: ${colors.cardBackgroundHover};

    p,
    h5 {
      color: ${colors.pureWhite};
    }
  }

  @media only screen and (max-width: ${media.mediumTop}) {
    width: 100%;
    padding: 28px;
  }
`;

const Content = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  z-index: 1;
`;

const ClientLabel = styled(Label)`
  transition: color ${animation.general.durationAndTiming};
`;

const TitleHeading = styled(Heading)`
  margin-bottom: 12px;
`;

const SnippetText = styled(Text)`
  margin-bottom: 8px;

  @media only screen and (max-width: ${media.largeBottom}) {
    display: flex;
  }
`;

const CategoriesText = styled(Text)`
  margin-top: auto;
  color: ${colors.gray1};
  font-style: italic;
`;

export default CaseStudyCard;
